import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleLogin } from '@react-oauth/google';
import type { CredentialResponse } from '@react-oauth/google';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { cn } from '@/styles/tailwind-utils';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { useInviteDetails } from '@features/staff/hooks/useInviteDetails';
import { useInviteGoogleRegister } from '@features/staff/hooks/useInviteGoogleRegister';
import type { InviteDetails } from '@features/staff/types';

// ─── Left-panel illustration ──────────────────────────────────────────────────

function ActivityRow({
  icon, title, subtitle, time, isLatest,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  time: string;
  isLatest?: boolean;
}) {
  return (
    <div
      className="flex items-start gap-3 rounded-lg px-3 py-2.5"
      style={{
        background: isLatest ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
        outline: isLatest ? '1px solid rgba(255,255,255,0.18)' : 'none',
      }}
    >
      <div
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ background: 'rgba(255,255,255,0.12)' }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="truncate text-[13px] font-medium"
          style={{ color: isLatest ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.75)' }}
        >
          {title}
        </div>
        <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {subtitle}
        </div>
      </div>
      <div
        className="shrink-0 pt-0.5 text-[10px]"
        style={{ color: isLatest ? '#6ee7b7' : 'rgba(255,255,255,0.3)' }}
      >
        {time}
      </div>
    </div>
  );
}

function SidebarIllustration() {
  return (
    <div className="relative flex flex-1 items-center justify-center overflow-hidden px-6">
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-28 -left-16 h-80 w-80 rounded-full"
        style={{ background: 'rgba(255,255,255,0.04)' }}
      />
      <div
        className="pointer-events-none absolute bottom-24 right-8 h-32 w-32 rounded-full"
        style={{ background: 'rgba(255,255,255,0.03)' }}
      />

      <div className="relative w-full max-w-[360px]">
        <div
          className="absolute -right-3 -top-4 z-10 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium"
          style={{ background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.85)' }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#6ee7b7]" />
          Live updates
        </div>

        <div
          className="rounded-2xl p-5"
          style={{
            background: 'rgba(255,255,255,0.09)',
            border: '1px solid rgba(255,255,255,0.13)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-[13px] font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                Recent activity
              </p>
              <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Today · 3 updates
              </p>
            </div>
            <div
              className="flex h-7 w-7 items-center justify-center rounded-lg"
              style={{ background: 'rgba(255,255,255,0.12)' }}
            >
              <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
                <path d="M8 2C5.79 2 4 3.79 4 6v3L2.5 11h11L12 9V6c0-2.21-1.79-4-4-4z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeLinejoin="round" />
                <path d="M6.5 11v.5a1.5 1.5 0 003 0V11" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
              </svg>
            </div>
          </div>

          <div className="space-y-1.5">
            <ActivityRow
              isLatest
              title="Appointment confirmed"
              subtitle="Maria Alexe · Massage · 09:00"
              time="just now"
              icon={
                <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden="true">
                  <path d="M2 6L5 9L10 3" stroke="#6ee7b7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            />
            <ActivityRow
              title="New booking"
              subtitle="Ion Popescu · Physio · Fri 14:00"
              time="2m ago"
              icon={
                <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden="true">
                  <rect x="1.5" y="2" width="9" height="8.5" rx="1.5" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" />
                  <path d="M4 1v2M8 1v2M1.5 5h9" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              }
            />
            <ActivityRow
              title="Invoice paid"
              subtitle="#INV-089 · €120.00"
              time="5m ago"
              icon={
                <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden="true">
                  <rect x="2" y="1" width="8" height="10" rx="1" stroke="#fde68a" strokeWidth="1.2" />
                  <path d="M4 4h4M4 6h4M4 8h2.5" stroke="#fde68a" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              }
            />
          </div>

          <div
            className="mt-4 grid grid-cols-3 gap-2 border-t pt-4"
            style={{ borderColor: 'rgba(255,255,255,0.10)' }}
          >
            {[
              { value: '12',  label: 'Today'      },
              { value: '94%', label: 'Attendance' },
              { value: '8',   label: 'Pending'    },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <div className="text-base font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  {value}
                </div>
                <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="absolute -bottom-4 -left-3 z-10 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium"
          style={{ background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.85)' }}
        >
          <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden="true">
            <path d="M2 6L5 9L10 3" stroke="#6ee7b7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Auto-reminders on
        </div>
      </div>
    </div>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function isGmail(email: string): boolean {
  return /@gmail\.com$/i.test(email);
}

type StrengthScore = 0 | 1 | 2 | 3 | 4;

function calcStrength(password: string): StrengthScore {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return Math.min(4, score) as StrengthScore;
}

const STRENGTH_COLOR: Record<StrengthScore, string> = {
  0: '',
  1: '#EF4444',
  2: '#F59E0B',
  3: '#84CC16',
  4: '#22C55E',
};

// ─── Shared icons ─────────────────────────────────────────────────────────────

function EyeIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <ellipse cx="8" cy="8" rx="6.5" ry="4.5" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path d="M2 2l12 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M6.5 3.8A7 7 0 018 3.5c4 0 6.5 4.5 6.5 4.5s-.7 1.4-2 2.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M4 5.4C2.2 6.7 1.5 8 1.5 8S4 12.5 8 12.5c1.2 0 2.3-.4 3.2-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M6.2 7.9a2 2 0 002.7 1.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

// ─── Right-panel content ──────────────────────────────────────────────────────

function LoadingContent() {
  return (
    <div className="space-y-4 p-8">
      <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
      <div className="h-7 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
      <div className="mt-6 space-y-3 rounded-lg border border-border p-4">
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

function ErrorContent({ code }: { code: string }) {
  const { t } = useTranslation();

  const isExpired = code === 'INVITE_EXPIRED';
  const isInvalid = code === 'INVITE_TOKEN_INVALID' || code === 'INVITE_NOT_FOUND';

  const title = isExpired
    ? t('inviteAccept.errors.expired')
    : isInvalid
      ? t('inviteAccept.errors.invalid')
      : t('inviteAccept.errors.generic');

  const body = isExpired
    ? t('inviteAccept.errors.expiredBody')
    : isInvalid
      ? t('inviteAccept.errors.invalidBody')
      : t('inviteAccept.errors.genericBody');

  return (
    <div className="p-8">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
        <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden="true">
          <circle cx="10" cy="10" r="9" stroke="#EF4444" strokeWidth="1.5" />
          <path d="M10 6v4.5M10 13.5v.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <h1 className="text-lg font-semibold text-foreground">{title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function PasswordStrengthBar({ password }: { password: string }) {
  const { t } = useTranslation();
  const score = calcStrength(password);
  const color = STRENGTH_COLOR[score];
  const label =
    score === 1 ? t('inviteAccept.form.strengthWeak')
    : score === 2 ? t('inviteAccept.form.strengthFair')
    : score === 3 ? t('inviteAccept.form.strengthGood')
    : score === 4 ? t('inviteAccept.form.strengthStrong')
    : '';

  if (!password) return null;

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {([1, 2, 3, 4] as const).map((i) => (
          <div
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full transition-all duration-300',
              i > score && 'bg-border',
            )}
            style={i <= score ? { backgroundColor: color } : undefined}
          />
        ))}
      </div>
      {label && (
        <p className="text-xs font-medium" style={{ color }}>
          {label}
        </p>
      )}
    </div>
  );
}

function InviteContent({ invite, token }: { invite: InviteDetails; token: string }) {
  const { t } = useTranslation();

  const isExpired    = new Date(invite.inviteExpiresAt) < new Date();
  const isGmailEmail = isGmail(invite.email);
  const daysLeft     = Math.max(0, Math.ceil(
    (new Date(invite.inviteExpiresAt).getTime() - Date.now()) / 86_400_000,
  ));

  const [showPassword,    setShowPassword]    = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const { register: registerWithGoogle, isPending, errorMessage } = useInviteGoogleRegister(token);

  const schema = useMemo(
    () =>
      z
        .object({
          password:        z.string().min(8, t('inviteAccept.form.passwordMinLength')),
          confirmPassword: z.string(),
        })
        .refine((d) => d.password === d.confirmPassword, {
          message: t('inviteAccept.form.passwordMismatch'),
          path: ['confirmPassword'],
        }),
    [t],
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  const passwordValue = watch('password') ?? '';

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      registerWithGoogle(credentialResponse.credential);
    }
  };

  const onSubmit = handleSubmit((_data) => {
    // TODO: Register with email + password when the API endpoint is available
  });

  return (
    <>
      {/* ── Branded header band ─────────────────────────────────────────── */}
      <div className="bg-brand-subtle px-8 py-6">
        <div className="mb-2 flex items-center gap-1.5">
          <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5 text-brand" aria-hidden="true">
            <rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.3" />
            <path d="M1 6l7 4.5L15 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <span className="text-xs font-semibold uppercase tracking-wider text-brand">
            {t('inviteAccept.header.badge')}
          </span>
        </div>
        <h1 className="text-xl font-bold text-foreground">{invite.accountName}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t('inviteAccept.header.subtitle', { firstName: invite.firstName })}
        </p>
      </div>

      {/* ── Form area ───────────────────────────────────────────────────── */}
      <div className="px-8 py-6">
        {isExpired ? (
          <div className="rounded-md bg-red-50 px-4 py-3">
            <p className="text-sm font-medium text-red-600">{t('inviteAccept.errors.expired')}</p>
            <p className="mt-0.5 text-xs text-red-500">{t('inviteAccept.errors.expiredBody')}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {errorMessage && (
              <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {errorMessage}
              </div>
            )}

            {/* Google button — Gmail accounts only */}
            {isGmailEmail && (
              <>
                <div
                  className={cn('flex justify-center', isPending && 'pointer-events-none opacity-60')}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => {/* errors surface via mutation.error */}}
                    login_hint={invite.email}
                    text="continue_with"
                    size="large"
                    shape="rectangular"
                    theme="outline"
                    width="396"
                  />
                </div>

                {!showPasswordForm ? (
                  <p className="text-center text-sm text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => setShowPasswordForm(true)}
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      {t('inviteAccept.form.usePasswordInstead')}
                    </button>
                  </p>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('inviteAccept.form.orContinueWithEmail')}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
              </>
            )}

            {/* Email + password form — always shown for non-Gmail, opt-in for Gmail */}
            {(!isGmailEmail || showPasswordForm) && (
              <form onSubmit={onSubmit} noValidate className="space-y-4">
                <Input
                  label={t('inviteAccept.form.emailLabel')}
                  type="email"
                  value={invite.email}
                  readOnly
                  tabIndex={-1}
                  className="cursor-default bg-muted text-muted-foreground"
                />

                <div>
                  <Input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    label={t('inviteAccept.form.passwordLabel')}
                    placeholder={t('inviteAccept.form.passwordPlaceholder')}
                    autoComplete="new-password"
                    error={errors.password?.message}
                    rightAddon={
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((p) => !p)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    }
                  />
                  <PasswordStrengthBar password={passwordValue} />
                </div>

                <Input
                  {...register('confirmPassword')}
                  type={showConfirm ? 'text' : 'password'}
                  label={t('inviteAccept.form.confirmPasswordLabel')}
                  placeholder={t('inviteAccept.form.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  error={errors.confirmPassword?.message}
                  rightAddon={
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                      onClick={() => setShowConfirm((p) => !p)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  }
                />

                <Button type="submit" size="lg" className="w-full justify-center">
                  {t('inviteAccept.form.createAccount')}
                </Button>
              </form>
            )}

            {/* Legal + expiry */}
            <p className="text-center text-xs text-muted-foreground">
              {t('inviteAccept.form.legalPrefix')}{' '}
              <a
                href="/terms"
                className="underline underline-offset-2 hover:text-foreground"
              >
                {t('inviteAccept.form.termsOfService')}
              </a>{' '}
              {t('inviteAccept.form.and')}{' '}
              <a
                href="/privacy"
                className="underline underline-offset-2 hover:text-foreground"
              >
                {t('inviteAccept.form.privacyPolicy')}
              </a>.
              {daysLeft > 0 && (
                <>
                  {' '}
                  {t('inviteAccept.form.expiresIn', { count: daysLeft })}
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { invite, isLoading, error } = useInviteDetails(token);

  return (
    <div className="flex min-h-screen">
      {/* Left panel – brand teal, visible on lg+ only */}
      <div
        className="hidden w-[540px] shrink-0 flex-col py-10 lg:flex"
        style={{ backgroundColor: 'hsl(160 51% 24%)' }}
      >
        <div className="flex items-center gap-2.5 px-10">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden="true">
              <rect x="2"  y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.9"  />
              <rect x="11" y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="2"  y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="11" y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.25" />
            </svg>
          </div>
          <span className="text-[15px] font-medium tracking-wide text-white">calendo</span>
        </div>

        <SidebarIllustration />

        <div className="px-10 pb-2">
          <p className="text-base font-semibold leading-snug text-white">
            Always in the loop.
            <br />
            <em className="font-light italic" style={{ color: 'rgba(255,255,255,0.65)' }}>
              without the noise.
            </em>
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex flex-1 flex-col items-center justify-center bg-background px-6 py-12 lg:px-16">
        {/* Mobile logo – hidden on lg where left panel shows */}
        <div className="mb-8 flex items-center gap-2 lg:hidden">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-sm"
            style={{ backgroundColor: 'hsl(160 51% 24%)' }}
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
              <rect x="2"  y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.9"  />
              <rect x="11" y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="2"  y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="11" y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.25" />
            </svg>
          </div>
          <span className="text-[15px] font-medium tracking-wide text-foreground">calendo</span>
        </div>

        {/* Card — overflow-hidden so the header band bleeds edge-to-edge */}
        <div className="w-full max-w-[460px] overflow-hidden rounded-md border border-border bg-surface shadow-lg">
          {!token          && <ErrorContent code="INVITE_TOKEN_INVALID" />}
          {token && isLoading && <LoadingContent />}
          {token && error     && <ErrorContent code={error.code} />}
          {token && invite    && <InviteContent invite={invite} token={token} />}
        </div>
      </div>
    </div>
  );
}
